import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, Tag as TagIcon, Hash } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { useTranslation } from '@/i18n';
import { useTags, useCreateTag, useDeleteTag } from '@/hooks/useTags';
import type { Tag, CreateTagInput } from '@/types/tag';
import { TAG_COLORS } from '@/types/tag';

export const TagsPage: React.FC = () => {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState<string>(TAG_COLORS[0]);

  const { data, isLoading, isError } = useTags();
  const createMutation = useCreateTag();
  const deleteMutation = useDeleteTag();

  const tags = data?.tags || [];

  const handleCreate = () => {
    setFormName('');
    setFormColor(TAG_COLORS[0]);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newTag: CreateTagInput = {
      name: formName.toLowerCase().replace(/\s+/g, '-'),
      color: formColor,
    };
    await createMutation.mutateAsync(newTag);
    setIsFormOpen(false);
  };

  const handleDelete = async () => {
    if (deletingTag) {
      await deleteMutation.mutateAsync(deletingTag.id);
      setDeletingTag(null);
    }
  };

  if (isLoading) {
    return <PageLoader message={t.app.tags.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.common.error}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.tags.error}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.app.tags.title}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.tags.subtitle}
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
          {t.app.tags.newTag}
        </Button>
      </div>

      {/* Info card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent>
          <div className="flex items-start gap-3">
            <Hash className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 dark:text-blue-100">
                {t.app.tags.howToUse}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {t.app.tags.howToUseDescription}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tags list */}
      {tags.length === 0 ? (
        <Card className="p-8 text-center">
          <TagIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t.app.tags.noTags}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-4">
            {t.app.tags.noTagsDescription}
          </p>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.app.tags.createFirst}
          </Button>
        </Card>
      ) : (
        <div className="flex flex-wrap gap-3">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="group flex items-center gap-2 px-4 py-2 bg-white dark:bg-secondary-800 rounded-full border border-gray-200 dark:border-secondary-700 hover:shadow-md transition-shadow"
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: tag.color }}
              />
              <span className="font-medium text-gray-900 dark:text-white">
                #{tag.name}
              </span>
              <Badge variant="secondary" size="sm">
                {tag.usageCount}
              </Badge>
              <button
                onClick={() => setDeletingTag(tag)}
                className="ml-1 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {tags.length > 0 && (
        <Card>
          <CardContent>
            <h3 className="font-medium text-gray-900 dark:text-white mb-4">
              {t.app.tags.stats.title}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {tags.length}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.app.tags.stats.created}
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {tags.reduce((sum, tag) => sum + tag.usageCount, 0)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.app.tags.stats.totalUsage}
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {tags.length > 0
                    ? tags.reduce((max, tag) => (tag.usageCount > max.usageCount ? tag : max), tags[0]).name
                    : '-'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.app.tags.stats.mostUsed}
                </p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {tags.filter((tg) => tg.usageCount === 0).length}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.app.tags.stats.unused}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={t.app.tags.newTag}
        size="sm"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <Input
            label={t.app.tags.form.name}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={t.app.tags.form.namePlaceholder}
            hint={t.app.tags.form.nameHint}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.app.common.color}
            </label>
            <div className="flex flex-wrap gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormColor(color)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formColor === color
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => setIsFormOpen(false)}>
              {t.app.common.cancel}
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              {t.app.common.create}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingTag}
        onClose={() => setDeletingTag(null)}
        title={t.app.tags.confirmDelete}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t.app.tags.deleteWarning}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              #{deletingTag?.name}
            </span>
            ?
          </p>
          {deletingTag && deletingTag.usageCount > 0 && (
            <p className="text-sm text-yellow-600 dark:text-yellow-500">
              {t.app.tags.deleteUsageWarning.replace('{count}', String(deletingTag.usageCount))}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeletingTag(null)}>
              {t.app.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              {t.app.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TagsPage;
